import { createClient } from '@feedfm/clips-services-api-client';
import type { ServicesApiClient } from '@feedfm/clips-services-api-client';
import { getToken } from '~/util/authentication';
import { environment } from '~/util/environment';

/**
 * Utility that returns a human-readable string from an unknown error for display in UI (e.g. inline
 * error state). Use with useAlert by passing { message: getErrorMessage(error) ?? 'Fallback', error
 * } for full details.
 */
export function getErrorMessage( error: unknown ): string {
  if ( error && typeof error === 'object' && 'message' in error && typeof error.message === 'string' ) {
    return error.message;
  }

  if ( error && typeof error === 'string' ) {
    return error;
  }

  return 'Unknown error response from Clips Services API';
}

/**
 * Appends the Feed Services auth token to requests, because we're speaking to the Feed Services
 * API first.
 */
function authMiddleware( options: { request: Request } ): Request {
  const token = getToken();

  if ( token ) {
    options.request.headers.set( 'Authorization', `Bearer ${token}` );
  }

  return options.request;
}

/**
 * The main output of this module -- a client configured to talk to the Clips Services API.
 * Components should use `useClipsClient` to obtain a managed copy of this client.
 * 
 * Returns the configured Clips Services API client. Use for all Clips API calls. Set identity via
 * client.setIdentity() so requests include X-FMG-Identity when present.
 */
export function getClipsClient(): ServicesApiClient {
  const client = createClient( {
    baseUrl: environment.backendUrl + '/clips',
  } );

  client.use( { onRequest: authMiddleware } );

  return client;
}

/** Utility to verify Clips API connectivity. */
async function clipsTest(): Promise<{ data?: unknown; error?: unknown }> {
  const client = getClipsClient();
  try {
    const result = await client.GET( '/protected' );

    if ( result.error ) {
      console.log( '[clipsTest] Error:', result.error );
      return result;
    }
    console.log( '[clipsTest] Success:', result.data );
    return result;
  } catch ( err ) {
    console.log( '[clipsTest] Exception:', err );
    throw err;
  }
}

declare global {
  interface Window {
    /** Exposes Clips test utility to the browser. */
    clipsTest?: typeof clipsTest;
  }
}

/** Exposes Clips test utility to the browser. */
export function installClipsTest() {
  if ( typeof window !== 'undefined' ) {
    window.clipsTest = clipsTest;
  }
}
